import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core'; // Added Signal import
import { Router } from '@angular/router';
import { CreateCspTenantRequest, CspTenant, UpdateCspTenantRequest } from '@models/csp/tenant.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntity, createStateResetHandler, deleteEntity, getAll, getOne, StateDictionary, updateEntity } from '../../helpers';
import { CspTenantsService } from './tenants.service';

type TenantsState = {
  tenants: CspTenant[];
  isLoading: boolean;
  selectedTenant: CspTenant | null;
  selectedTenantLoading: boolean;
  tenantCreateLoading: boolean;
};

const initialState: TenantsState = {
  tenants: [],
  isLoading: false,
  selectedTenant: null,
  selectedTenantLoading: false,
  tenantCreateLoading: false,
};

const tenantsDictionary: StateDictionary<CspTenant> = {
  entityList: 'tenants',
  listLoading: 'isLoading',
  createLoading: 'tenantCreateLoading',
  selectedEntity: 'selectedTenant',
  selectedEntityLoading: 'selectedTenantLoading',
  entityId: 'id',
  sortFn: (a: CspTenant, b: CspTenant) => a.name.localeCompare(b.name),
};

export const TenantsStore = signalStore(
  withState(initialState),
  withDevtools('tenants-store'),
  withComputed(store => ({
    selectedTenantId: computed(() => store.selectedTenant()?.id ?? null),
  })),
  withMethods((store, tenantsService = inject(CspTenantsService), router = inject(Router)) => {
    createStateResetHandler<TenantsState>('/csp/tenants')(store, initialState);

    const repositoryMethods = {
      getAll: getAll<CspTenant>({
        store,
        service: tenantsService,
        dictionary: tenantsDictionary,
      }),

      getOne: getOne<CspTenant>({
        store,
        service: tenantsService,
        dictionary: tenantsDictionary,
      }),

      addTenant: addEntity<CspTenant, CreateCspTenantRequest>({
        store,
        service: tenantsService,
        dictionary: tenantsDictionary,
        onSuccessCallback: (newTenant: CspTenant) => {
          patchState(store, { selectedTenant: newTenant });
          router.navigate(['csp', 'tenants', newTenant.id, 'details']);
        },
      }),

      updateTenant: updateEntity<CspTenant, UpdateCspTenantRequest>({
        store,
        service: tenantsService,
        dictionary: tenantsDictionary,
        onSuccessCallback: (updatedTenant: CspTenant) => {
          patchState(store, { selectedTenant: updatedTenant });
          repositoryMethods.getAll();
        },
      }),

      deleteTenant: deleteEntity<CspTenant>({
        store,
        service: tenantsService,
        dictionary: tenantsDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedTenant: null });
          router.navigate(['csp', 'tenants']);
          repositoryMethods.getAll();
        },
      }),
    };

    const navigationActions = {
      goToTenantActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['csp', 'tenants', id]);
        if (!result) return false;
        if (!store.selectedTenantLoading()) repositoryMethods.getOne(id);
        return true;
      },

      goToListPage: (): void => {
        patchState(store, { selectedTenant: null });
        router.navigate(['csp', 'tenants']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface TenantsStoreType {
  readonly tenants: Signal<CspTenant[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedTenant: Signal<CspTenant | null>;
  readonly selectedTenantLoading: Signal<boolean>;
  readonly tenantCreateLoading: Signal<boolean>;

  readonly selectedTenantId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly addTenant: (dto: CreateCspTenantRequest) => void;
  readonly updateTenant: (input: { id: string; dto: UpdateCspTenantRequest }) => void;
  readonly deleteTenant: (id: string) => void;
  readonly goToTenantActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
}
